// resources/js/components/admin/ratings/ViewRatings.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
  InputGroup,
  FormControl,
  Pagination,
  Dropdown,
} from "react-bootstrap";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import api from "../../services/api";
import { Colors } from "../../constants/Colors";

/* ================================================================== */
/* CONSTANTS                                                          */
/* ================================================================== */

const MySwal = withReactContent(Swal);
const PER_PAGE = 12;
const SEARCH_DEBOUNCE_MS = 400;
const MAX_RESPONSE_LENGTH = 1000;

const T = {
  radius: 14,
  radiusSm: 10,
  shadow: "0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06)",
  shadowHover: "0 4px 8px rgba(16,24,40,.06), 0 12px 28px rgba(16,24,40,.10)",
  border: `1px solid ${Colors.BORDER_GRAY}`,
  font: "'Inter', 'Segoe UI', system-ui, sans-serif",
};

const STAR_YELLOW = Colors.STAR_YELLOW || "#FFC107";
const SECONDARY_BLUE = Colors.SECONDARY_BLUE || Colors.PRIMARY || "#0E6BF8";

const CATEGORIES = ["service", "driver", "punctuality", "cleanliness"];

const CATEGORY_META = {
  service: { icon: "bi-handshake", label: "Service Quality" },
  driver: { icon: "bi-person-badge", label: "Driver Professionalism" },
  punctuality: { icon: "bi-clock", label: "Punctuality" },
  cleanliness: { icon: "bi-brush", label: "Cleanliness" },
};

const RATING_LABELS = {
  1: "Very Poor",
  2: "Poor",
  3: "Average",
  4: "Good",
  5: "Excellent",
};

const EMPTY_STATS = {
  total_ratings: 0,
  average_rating: 0,
  rating_breakdown: {},
  pending_responses: 0,
  responded_ratings: 0,
  category_averages: {
    service: 0,
    driver: 0,
    punctuality: 0,
    cleanliness: 0,
  },
  popular_tags: {},
  ratings_with_images: 0,
};

/* ================================================================== */
/* HELPERS                                                            */
/* ================================================================== */

const renderStars = (score, small = false) => {
  const cls = small ? "bk-stars bk-stars-sm" : "bk-stars";
  const safe = Math.max(0, Math.min(5, Math.round(Number(score) || 0)));
  return (
    <span className={cls}>
      {[1, 2, 3, 4, 5].map((i) => (
        <i
          key={i}
          className={`bi ${i <= safe ? "bi-star-fill" : "bi-star"}`}
          style={{ color: i <= safe ? STAR_YELLOW : Colors.GRAY }}
        />
      ))}
    </span>
  );
};

const getRatingLabel = (score) =>
  RATING_LABELS[Math.round(Number(score) || 0)] || "N/A";

const getRatingMeta = (score) => {
  const s = Number(score) || 0;
  if (s <= 2) return { color: Colors.RED, bg: `${Colors.RED}1f` };
  if (s === 3) return { color: Colors.WARNING, bg: `${Colors.WARNING}1f` };
  return { color: Colors.SUCCESS, bg: `${Colors.SUCCESS}1f` };
};

const getCategoryIcon = (category) =>
  CATEGORY_META[category]?.icon || "bi-star";

const getCategoryLabel = (category) =>
  CATEGORY_META[category]?.label || category;

const initials = (name = "") => {
  const parts = String(name).trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getImageUrl = (img) => {
  if (!img) return null;
  if (typeof img === "string") return img;
  if (typeof img === "object") return img.url || img.path || null;
  return null;
};

const normalizeTags = (tags) => {
  if (Array.isArray(tags)) return tags.filter((t) => typeof t === "string");
  if (typeof tags === "string") {
    try {
      const parsed = JSON.parse(tags);
      return Array.isArray(parsed)
        ? parsed.filter((t) => typeof t === "string")
        : [];
    } catch {
      return [];
    }
  }
  return [];
};

/* ================================================================== */
/* STYLES                                                             */
/* ================================================================== */

const Styles = () => (
  <style>{`
    .bk-page { font-family: ${T.font}; background: ${Colors.LIGHT_GRAY}; min-height: 100vh; padding: 24px 0 60px; }
    .bk-page .container-fluid { max-width: 1400px; }
    .bk-header { background: linear-gradient(135deg, ${Colors.GRADIENT_START} 0%, ${Colors.GRADIENT_END} 100%); color: #fff; border-radius: ${T.radius}px; padding: 26px 30px; margin-bottom: 20px; position: relative; overflow: hidden; box-shadow: 0 10px 30px rgba(14,107,248,.22); }
    .bk-header::after { content: ""; position: absolute; top: -60px; right: -60px; width: 220px; height: 220px; background: radial-gradient(circle, rgba(255,255,255,.18) 0%, transparent 70%); border-radius: 50%; }
    .bk-header h2 { font-weight: 700; letter-spacing: -.02em; margin: 0; font-size: 22px; }
    .bk-header p { opacity: .88; margin: 4px 0 0; font-size: 13.5px; }
    .bk-header-rating { position: relative; z-index: 1; background: rgba(255,255,255,.16); border: 1px solid rgba(255,255,255,.28); backdrop-filter: blur(8px); border-radius: ${T.radiusSm}; padding: 12px 18px; text-align: center; }
    .bk-header-rating .val { font-size: 24px; font-weight: 700; line-height: 1; }
    .bk-header-rating .lbl { opacity: .85; font-size: 12px; margin-top: 3px; }
    .bk-stat { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 16px 18px; display: flex; align-items: center; gap: 12px; transition: transform .18s ease, box-shadow .18s ease; height: 100%; }
    .bk-stat:hover { transform: translateY(-2px); box-shadow: ${T.shadowHover}; }
    .bk-stat-icon { width: 44px; height: 44px; border-radius: 11px; display: grid; place-items: center; font-size: 19px; flex-shrink: 0; }
    .bk-stat-val { font-size: 21px; font-weight: 700; line-height: 1; color: ${Colors.TEXT_DARK}; }
    .bk-stat-lbl { font-size: 11px; color: ${Colors.TEXT_LIGHT}; text-transform: uppercase; letter-spacing: .05em; margin-top: 4px; font-weight: 600; }
    .bk-card { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; box-shadow: ${T.shadow}; overflow: hidden; }
    .bk-card-header { padding: 16px 22px; border-bottom: ${T.border}; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; background: #fff; }
    .bk-card-header h5 { margin: 0; font-weight: 600; color: ${Colors.TEXT_DARK}; font-size: 15.5px; display: flex; align-items: center; gap: 8px; }
    .bk-card-header small { font-size: 12.5px; color: ${Colors.TEXT_LIGHT}; }
    .bk-toolbar { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 12px; box-shadow: ${T.shadow}; margin-bottom: 18px; }
    .bk-input { border: 1px solid transparent !important; background: ${Colors.LIGHT_GRAY} !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; transition: all .18s; color: ${Colors.TEXT_DARK}; height: auto !important; }
    .bk-input:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: 0 0 0 3px ${Colors.PRIMARY}22 !important; }
    .bk-input-icon { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; border-right: none !important; border-radius: ${T.radiusSm} 0 0 ${T.radiusSm} !important; color: ${Colors.GRAY}; }
    .bk-filter-btn { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; font-weight: 500; text-align: left; display: flex; align-items: center; justify-content: space-between; height: auto !important; min-height: 42px; }
    .bk-filter-btn:hover, .bk-filter-btn:focus, .bk-filter-btn.show { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: none !important; color: ${Colors.TEXT_DARK} !important; }
    .bk-filter-btn::after { margin-left: auto; }
    .bk-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 11px; border-radius: 999px; font-size: 11.5px; font-weight: 600; line-height: 1.4; }
    .bk-pill::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
    .bk-pill.no-dot::before { display: none; }
    .bk-tag { display: inline-flex; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; background: ${Colors.PRIMARY}1a; color: ${Colors.PRIMARY}; cursor: pointer; }
    .bk-empty { text-align: center; padding: 60px 20px; }
    .bk-empty-icon { width: 80px; height: 80px; margin: 0 auto 18px; border-radius: 50%; background: ${Colors.LIGHT_GRAY}; display: grid; place-items: center; font-size: 34px; color: ${Colors.GRAY}; }
    .bk-empty h5 { font-weight: 600; margin-bottom: 6px; }
    .bk-skel { background: linear-gradient(90deg, ${Colors.LIGHT_GRAY} 25%, #eee 50%, ${Colors.LIGHT_GRAY} 75%); background-size: 200% 100%; animation: bk-shimmer 1.4s infinite; border-radius: 6px; }
    @keyframes bk-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
    .bk-btn-primary { background: ${Colors.PRIMARY} !important; border: none !important; color: #fff !important; border-radius: ${T.radiusSm} !important; font-weight: 600; padding: 8px 16px; font-size: 13.5px; transition: all .18s; }
    .bk-btn-primary:hover:not(:disabled) { background: #e87a2e !important; transform: translateY(-1px); box-shadow: 0 6px 16px ${Colors.PRIMARY}44; }
    .bk-btn-primary:disabled { opacity: .6 !important; cursor: not-allowed !important; }
    .bk-btn-ghost { background: transparent !important; border: 1px solid ${Colors.BORDER_GRAY} !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; font-weight: 500; padding: 7px 13px; font-size: 13px; transition: all .18s; }
    .bk-btn-ghost:hover { border-color: ${Colors.PRIMARY} !important; color: ${Colors.PRIMARY} !important; background: ${Colors.PRIMARY}0d !important; }
    .bk-btn-ghost:disabled { opacity: .6 !important; cursor: not-allowed !important; }
    .bk-btn-danger { background: transparent !important; border: 1px solid ${Colors.RED} !important; color: ${Colors.RED} !important; border-radius: ${T.radiusSm} !important; font-weight: 500; padding: 7px 13px; font-size: 13px; transition: all .18s; }
    .bk-btn-danger:hover { background: ${Colors.RED}0d !important; }
    .bk-section { background: ${Colors.LIGHT_GRAY}; border-radius: ${T.radiusSm}; padding: 16px; margin-bottom: 14px; }
    .bk-section-title { font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; margin-bottom: 12px; display: flex; align-items: center; gap: 7px; }
    .bk-section-title i { color: ${Colors.PRIMARY}; font-size: 14px; }
    .bk-rating-card { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 18px; height: 100%; display: flex; flex-direction: column; transition: transform .18s ease, box-shadow .18s ease; }
    .bk-rating-card:hover { transform: translateY(-2px); box-shadow: ${T.shadowHover}; }
    .bk-rating-avatar { width: 40px; height: 40px; border-radius: 10px; background: linear-gradient(135deg, ${Colors.GRADIENT_START}, ${Colors.GRADIENT_END}); color: #fff; font-weight: 600; font-size: 14px; display: grid; place-items: center; flex-shrink: 0; }
    .bk-cat-box { background: ${Colors.LIGHT_GRAY}; border-radius: 8px; padding: 7px 10px; font-size: 12px; display: flex; justify-content: space-between; align-items: center; gap: 6px; }
    .bk-cat-label { color: ${Colors.TEXT_LIGHT}; }
    .bk-stars { color: ${STAR_YELLOW}; font-size: 13.5px; letter-spacing: 1px; }
    .bk-stars-sm { font-size: 11px; }
    .bk-thumb { width: 58px; height: 58px; border-radius: 8px; object-fit: cover; cursor: pointer; border: ${T.border}; transition: transform .15s; }
    .bk-thumb:hover { transform: scale(1.05); }
    .bk-thumb-wrap { position: relative; }
    .bk-thumb-overlay { position: absolute; inset: 0; background: rgba(0,0,0,.55); color: #fff; border-radius: 8px; display: grid; place-items: center; font-weight: 600; font-size: 13px; }
    .bk-response-box { background: rgba(14,107,248,.08); border: 1px solid rgba(14,107,248,.2); border-radius: 10px; padding: 12px 14px; font-size: 13px; }
    .bk-comment-box { background: ${Colors.LIGHT_GRAY}; border-radius: 10px; padding: 12px 14px; font-size: 13px; color: ${Colors.TEXT_DARK}; }
    .bk-dist-row { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; font-size: 13px; }
    .bk-dist-bar { flex: 1; height: 8px; background: ${Colors.LIGHT_GRAY}; border-radius: 4px; overflow: hidden; }
    .bk-dist-fill { height: 100%; background: ${Colors.WARNING}; border-radius: 4px; transition: width .3s; }
    .modal-content { border-radius: ${T.radius}px; border: none; box-shadow: 0 24px 60px rgba(16,24,40,.18); }
    .modal-header { border-bottom: ${T.border}; padding: 18px 22px; }
    .modal-title { font-weight: 700; color: ${Colors.TEXT_DARK}; font-size: 16px; }
    .modal-body { padding: 20px 22px; }
    .modal-footer { border-top: ${T.border}; padding: 14px 22px; }
    .page-item.active .page-link { background: ${Colors.PRIMARY} !important; border-color: ${Colors.PRIMARY} !important; color: #fff !important; }
    .page-link { color: ${Colors.TEXT_DARK} !important; border-color: ${Colors.BORDER_GRAY} !important; }
    .page-link:hover { background: ${Colors.LIGHT_GRAY} !important; }
    .dropdown-menu { border-radius: ${T.radiusSm}; border: ${T.border}; box-shadow: ${T.shadow}; padding: 6px; }
    .dropdown-item { border-radius: 6px; padding: 8px 12px; font-size: 13px; }
    .dropdown-item.active { background: ${Colors.PRIMARY}1a !important; color: ${Colors.TEXT_DARK} !important; }
    .spin { animation: bk-spin 1s linear infinite; display: inline-block; }
    @keyframes bk-spin { to { transform: rotate(360deg); } }
    .bk-image-modal .modal-content { background: transparent; border: none; box-shadow: none; }
    .bk-image-nav { position: absolute; top: 50%; transform: translateY(-50%); width: 44px; height: 44px; border-radius: 50%; background: rgba(255,255,255,.9); border: none; display: grid; place-items: center; color: #000; }
    .bk-image-nav:hover { background: #fff; }
  `}</style>
);

/* ================================================================== */
/* PRESENTATIONAL COMPONENTS                                          */
/* ================================================================== */

const StatsRow = ({ stats }) => {
  const items = [
    {
      k: "Pending Responses",
      v: stats.pending_responses || 0,
      i: "bi-clock-history",
      c: Colors.WARNING,
    },
    {
      k: "Responded Reviews",
      v: stats.responded_ratings || 0,
      i: "bi-check-circle",
      c: Colors.SUCCESS,
    },
    {
      k: "With Photos",
      v: stats.ratings_with_images || 0,
      i: "bi-image",
      c: Colors.INFO,
    },
    {
      k: "Unique Tags",
      v: Object.keys(stats.popular_tags || {}).length || 0,
      i: "bi-tags",
      c: Colors.PRIMARY,
    },
  ];

  return (
    <Row className="g-3 mb-4">
      {items.map((s) => (
        <Col key={s.k} xs={6} md={3}>
          <div className="bk-stat">
            <div
              className="bk-stat-icon"
              style={{ background: `${s.c}1f`, color: s.c }}
            >
              <i className={`bi ${s.i}`} />
            </div>
            <div>
              <div className="bk-stat-val">{s.v}</div>
              <div className="bk-stat-lbl">{s.k}</div>
            </div>
          </div>
        </Col>
      ))}
    </Row>
  );
};

const CategoryAverages = ({ averages }) => (
  <Row className="g-3">
    {Object.entries(averages).map(([key, value]) => (
      <Col key={key} xs={6}>
        <div className="text-center">
          <i
            className={`bi ${getCategoryIcon(key)}`}
            style={{ fontSize: 22, color: Colors.PRIMARY }}
          />
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: Colors.TEXT_DARK,
              marginTop: 6,
            }}
          >
            {parseFloat(value || 0).toFixed(1)}
          </div>
          <small className="text-muted">{getCategoryLabel(key)}</small>
        </div>
      </Col>
    ))}
  </Row>
);

const RatingDistribution = ({ breakdown }) => {
  const maxCount = Math.max(...Object.values(breakdown || { 1: 1 }), 1);
  return (
    <>
      {[5, 4, 3, 2, 1].map((star) => {
        const count = breakdown[star] || 0;
        const pct = (count / maxCount) * 100;
        return (
          <div key={star} className="bk-dist-row">
            <span style={{ minWidth: 45 }}>
              {star}{" "}
              <i className="bi bi-star-fill" style={{ color: STAR_YELLOW }} />
            </span>
            <div className="bk-dist-bar">
              <div className="bk-dist-fill" style={{ width: `${pct}%` }} />
            </div>
            <strong
              style={{
                minWidth: 30,
                textAlign: "right",
                color: Colors.TEXT_DARK,
              }}
            >
              {count}
            </strong>
          </div>
        );
      })}
    </>
  );
};

const PopularTags = ({ tags, onTagClick }) => (
  <div className="d-flex flex-wrap gap-2">
    {Object.entries(tags)
      .slice(0, 12)
      .map(([tag, count]) => (
        <span key={tag} className="bk-tag" onClick={() => onTagClick(tag)}>
          #{tag} <span style={{ marginLeft: 4, opacity: 0.7 }}>{count}</span>
        </span>
      ))}
  </div>
);

/* ================================================================== */
/* MAIN COMPONENT                                                     */
/* ================================================================== */

const ViewRatings = () => {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(EMPTY_STATS);
  const [meta, setMeta] = useState({});

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filterScore, setFilterScore] = useState("");
  const [filterResponseStatus, setFilterResponseStatus] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [filterHasImages, setFilterHasImages] = useState("");

  const [showAdminResponseModal, setShowAdminResponseModal] = useState(false);
  const [selectedRating, setSelectedRating] = useState(null);
  const [adminResponse, setAdminResponse] = useState("");
  const [submittingResponse, setSubmittingResponse] = useState(false);
  const [responseError, setResponseError] = useState("");

  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  /* ---------------- Debounced search ---------------- */
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  /* ---------------- Fetch ---------------- */
  const fetchRatings = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        page: String(page),
        per_page: String(PER_PAGE),
      });

      if (search) params.append("search", search);
      if (filterScore) params.append("rating", filterScore);
      if (filterResponseStatus)
        params.append("admin_response_status", filterResponseStatus);
      if (filterCategory) params.append("category", filterCategory);
      if (filterTag) params.append("tag", filterTag);
      if (filterHasImages) params.append("has_images", filterHasImages);

      const response = await api.get(`/admin/ratings?${params.toString()}`, {
        signal: controller.signal,
      });

      if (response.data.success) {
        setRatings(response.data.data || []);
        setMeta(response.data.meta || {});
        setStats((prev) => ({ ...prev, ...(response.data.stats || {}) }));
        setTotalPages(response.data.meta?.last_page || 1);
      } else {
        setError(response.data.message || "Failed to fetch ratings");
        setRatings([]);
      }
    } catch (err) {
      if (err.name === "AbortError" || err.code === "ERR_CANCELED") return;
      console.error("Error fetching ratings:", err);
      setError(err.response?.data?.message || "Failed to fetch ratings");
      setRatings([]);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    search,
    filterScore,
    filterResponseStatus,
    filterCategory,
    filterTag,
    filterHasImages,
  ]);

  useEffect(() => {
    fetchRatings();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchRatings]);

  /* ---------------- Handlers ---------------- */
  const handleFilterChange = (type, value) => {
    setPage(1);
    switch (type) {
      case "score":
        setFilterScore(value);
        break;
      case "response":
        setFilterResponseStatus(value);
        break;
      case "category":
        setFilterCategory(value);
        break;
      case "tag":
        setFilterTag(value);
        break;
      case "images":
        setFilterHasImages(value);
        break;
      default:
        break;
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleReset = () => {
    setSearchInput("");
    setSearch("");
    setFilterScore("");
    setFilterResponseStatus("");
    setFilterCategory("");
    setFilterTag("");
    setFilterHasImages("");
    setPage(1);
  };

  /* ---------------- Admin response ---------------- */
  const openAdminResponseModal = (rating) => {
    setSelectedRating(rating);
    setAdminResponse(rating.admin_response || "");
    setShowAdminResponseModal(true);
    setResponseError("");
  };

  const closeAdminResponseModal = () => {
    if (submittingResponse) return;
    setShowAdminResponseModal(false);
    setSelectedRating(null);
    setAdminResponse("");
    setResponseError("");
  };

  const submitAdminResponse = async () => {
    if (!selectedRating || !adminResponse.trim()) {
      setResponseError("Please enter a response");
      return;
    }

    try {
      setSubmittingResponse(true);
      setResponseError("");

      const endpoint = `/admin/ratings/${selectedRating.rating_id}/admin-response`;
      const hasExisting = !!selectedRating.admin_response;

      // axios instance is callable; but also support .post/.put
      let response;
      if (typeof api === "function") {
        response = await api({
          method: hasExisting ? "PUT" : "POST",
          url: endpoint,
          data: { admin_response: adminResponse.trim() },
        });
      } else {
        response = hasExisting
          ? await api.put(endpoint, { admin_response: adminResponse.trim() })
          : await api.post(endpoint, { admin_response: adminResponse.trim() });
      }

      if (response.data.success) {
        const saved = response.data.data || {};
        setRatings((prev) =>
          prev.map((r) =>
            r.rating_id === selectedRating.rating_id
              ? {
                  ...r,
                  admin_response: saved.admin_response ?? adminResponse.trim(),
                  admin_response_at:
                    saved.admin_response_at ?? new Date().toISOString(),
                  admin_responded_at:
                    saved.admin_responded_at ?? new Date().toISOString(),
                  has_admin_response: true,
                }
              : r,
          ),
        );

        if (!hasExisting) {
          setStats((prev) => ({
            ...prev,
            pending_responses: Math.max(0, (prev.pending_responses || 0) - 1),
            responded_ratings: (prev.responded_ratings || 0) + 1,
          }));
        }

        closeAdminResponseModal();
        MySwal.fire({
          icon: "success",
          title: "Response saved",
          timer: 1800,
          showConfirmButton: false,
        });
      } else {
        setResponseError(response.data.message || "Failed to submit response");
      }
    } catch (err) {
      console.error("Error submitting admin response:", err);
      const msg =
        err.response?.data?.message ||
        (err.response?.status === 409
          ? "A response already exists. Refresh and try editing."
          : "Failed to submit response");
      setResponseError(msg);
    } finally {
      setSubmittingResponse(false);
    }
  };

  const deleteAdminResponse = async (ratingId) => {
    const result = await MySwal.fire({
      title: "Delete admin response?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: Colors.RED,
      cancelButtonColor: Colors.GRAY,
      confirmButtonText: "Yes, delete",
    });
    if (!result.isConfirmed) return;

    try {
      const response =
        typeof api === "function"
          ? await api({
              method: "DELETE",
              url: `/admin/ratings/${ratingId}/admin-response`,
            })
          : await api.delete(`/admin/ratings/${ratingId}/admin-response`);

      if (response.data.success) {
        setRatings((prev) =>
          prev.map((r) =>
            r.rating_id === ratingId
              ? {
                  ...r,
                  admin_response: null,
                  admin_response_at: null,
                  admin_responded_at: null,
                  has_admin_response: false,
                }
              : r,
          ),
        );
        setStats((prev) => ({
          ...prev,
          pending_responses: (prev.pending_responses || 0) + 1,
          responded_ratings: Math.max(0, (prev.responded_ratings || 0) - 1),
        }));
        MySwal.fire({
          icon: "success",
          title: "Deleted",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error("Error deleting admin response:", err);
      MySwal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Failed to delete admin response",
      });
    }
  };

  /* ---------------- Image gallery ---------------- */
  const openImageGallery = (images) => {
    if (!images || images.length === 0) return;
    setSelectedImages(images);
    setCurrentImageIndex(0);
    setShowImageModal(true);
  };

  const nextImage = () =>
    setCurrentImageIndex((p) => (p < selectedImages.length - 1 ? p + 1 : 0));

  const prevImage = () =>
    setCurrentImageIndex((p) => (p > 0 ? p - 1 : selectedImages.length - 1));

  /* ---------------- Pagination ---------------- */
  const renderPaginationItems = () => {
    const items = [];
    const max = 5;
    let start = Math.max(1, page - Math.floor(max / 2));
    const end = Math.min(totalPages, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    for (let n = start; n <= end; n++) {
      items.push(
        <Pagination.Item
          key={n}
          active={n === page}
          onClick={() => handlePageChange(n)}
        >
          {n}
        </Pagination.Item>,
      );
    }
    return items;
  };

  const hasActiveFilters =
    !!search ||
    !!filterScore ||
    !!filterResponseStatus ||
    !!filterCategory ||
    !!filterTag ||
    !!filterHasImages;

  /* ---------------- Loading ---------------- */
  if (loading && ratings.length === 0) {
    return (
      <div className="bk-page">
        <Styles />
        <Container fluid>
          <div className="bk-header">
            <h2>Ratings &amp; Reviews</h2>
            <p>Loading customer feedback…</p>
          </div>
          <Row className="g-3 mb-4">
            {[1, 2, 3, 4].map((i) => (
              <Col key={i} xs={6} md={3}>
                <div className="bk-stat">
                  <div
                    className="bk-skel"
                    style={{ width: 44, height: 44, borderRadius: 11 }}
                  />
                  <div className="flex-grow-1">
                    <div
                      className="bk-skel mb-2"
                      style={{ height: 18, width: "50%" }}
                    />
                    <div
                      className="bk-skel"
                      style={{ height: 11, width: "70%" }}
                    />
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </div>
    );
  }

  /* ---------------- Main render ---------------- */
  return (
    <div className="bk-page">
      <Styles />
      <Container fluid>
        {/* HEADER */}
        <div className="bk-header">
          <div
            className="position-relative d-flex justify-content-between align-items-center flex-wrap gap-3"
            style={{ zIndex: 1 }}
          >
            <div>
              <h2>Ratings &amp; Reviews Management</h2>
              <p>Manage customer feedback with enhanced features</p>
            </div>
            <div className="bk-header-rating" style={{ minWidth: 180 }}>
              <div className="val">
                <i
                  className="bi bi-star-fill me-2"
                  style={{ color: STAR_YELLOW }}
                />
                {(stats.total_ratings || 0) === 0
                  ? "—"
                  : parseFloat(stats.average_rating || 0).toFixed(1)}
              </div>
              <div className="lbl">
                {stats.total_ratings || 0} total reviews
              </div>
            </div>
          </div>
        </div>

        {/* ALERTS */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError("")}>
            <i className="bi bi-exclamation-triangle me-2" />
            {error}
          </Alert>
        )}

        {/* STATS */}
        <StatsRow stats={stats} />

        {/* CATEGORY AVG + DISTRIBUTION */}
        <Row className="g-3 mb-4">
          {stats.category_averages && (
            <Col lg={6}>
              <div className="bk-card" style={{ height: "100%" }}>
                <div className="bk-card-header">
                  <h5>
                    <i
                      className="bi bi-bar-chart"
                      style={{ color: Colors.PRIMARY }}
                    />
                    Category Averages
                  </h5>
                </div>
                <div style={{ padding: 20 }}>
                  <CategoryAverages averages={stats.category_averages} />
                </div>
              </div>
            </Col>
          )}

          {stats.rating_breakdown &&
            Object.keys(stats.rating_breakdown).length > 0 && (
              <Col lg={6}>
                <div className="bk-card" style={{ height: "100%" }}>
                  <div className="bk-card-header">
                    <h5>
                      <i
                        className="bi bi-graph-up"
                        style={{ color: Colors.PRIMARY }}
                      />
                      Rating Distribution
                    </h5>
                  </div>
                  <div style={{ padding: 20 }}>
                    <RatingDistribution breakdown={stats.rating_breakdown} />
                  </div>
                </div>
              </Col>
            )}
        </Row>

        {/* POPULAR TAGS */}
        {stats.popular_tags && Object.keys(stats.popular_tags).length > 0 && (
          <div className="bk-card mb-4">
            <div className="bk-card-header">
              <h5>
                <i className="bi bi-tags" style={{ color: Colors.PRIMARY }} />
                Popular Tags
              </h5>
            </div>
            <div style={{ padding: 20 }}>
              <PopularTags
                tags={stats.popular_tags}
                onTagClick={(tag) => handleFilterChange("tag", tag)}
              />
            </div>
          </div>
        )}

        {/* FILTERS */}
        <div className="bk-toolbar">
          <Row className="g-2 align-items-center">
            <Col xs={12} md={3}>
              <InputGroup>
                <InputGroup.Text className="bk-input-icon">
                  <i className="bi bi-search" />
                </InputGroup.Text>
                <FormControl
                  className="bk-input"
                  placeholder="Search ratings…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </InputGroup>
            </Col>

            <Col xs={6} md={2}>
              <Dropdown>
                <Dropdown.Toggle className="bk-filter-btn w-100">
                  <i className="bi bi-star me-2" />
                  {filterScore
                    ? `${filterScore} Star${filterScore > 1 ? "s" : ""}`
                    : "All Ratings"}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  <Dropdown.Item
                    active={!filterScore}
                    onClick={() => handleFilterChange("score", "")}
                  >
                    All Ratings
                  </Dropdown.Item>
                  {[5, 4, 3, 2, 1].map((n) => (
                    <Dropdown.Item
                      key={n}
                      active={filterScore === String(n)}
                      onClick={() => handleFilterChange("score", String(n))}
                    >
                      {"★".repeat(n)}
                      {"☆".repeat(5 - n)} ({n})
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col xs={6} md={2}>
              <Dropdown>
                <Dropdown.Toggle className="bk-filter-btn w-100">
                  <i className="bi bi-grid me-2" />
                  {filterCategory
                    ? getCategoryLabel(filterCategory)
                    : "All Categories"}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  <Dropdown.Item
                    active={!filterCategory}
                    onClick={() => handleFilterChange("category", "")}
                  >
                    All Categories
                  </Dropdown.Item>
                  {CATEGORIES.map((c) => (
                    <Dropdown.Item
                      key={c}
                      active={filterCategory === c}
                      onClick={() => handleFilterChange("category", c)}
                    >
                      {getCategoryLabel(c)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col xs={6} md={2}>
              <Dropdown>
                <Dropdown.Toggle className="bk-filter-btn w-100">
                  <i className="bi bi-reply me-2" />
                  {filterResponseStatus === "pending"
                    ? "Pending Response"
                    : filterResponseStatus === "responded"
                      ? "Admin Responded"
                      : "All Reviews"}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  <Dropdown.Item
                    active={!filterResponseStatus}
                    onClick={() => handleFilterChange("response", "")}
                  >
                    All Reviews
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={filterResponseStatus === "pending"}
                    onClick={() => handleFilterChange("response", "pending")}
                  >
                    Pending Response
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={filterResponseStatus === "responded"}
                    onClick={() => handleFilterChange("response", "responded")}
                  >
                    Admin Responded
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col xs={6} md={2}>
              <Dropdown>
                <Dropdown.Toggle className="bk-filter-btn w-100">
                  <i className="bi bi-image me-2" />
                  {filterHasImages === "with_images"
                    ? "With Photos"
                    : filterHasImages === "without_images"
                      ? "Without Photos"
                      : "All Reviews"}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  <Dropdown.Item
                    active={!filterHasImages}
                    onClick={() => handleFilterChange("images", "")}
                  >
                    All Reviews
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={filterHasImages === "with_images"}
                    onClick={() => handleFilterChange("images", "with_images")}
                  >
                    With Photos
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={filterHasImages === "without_images"}
                    onClick={() =>
                      handleFilterChange("images", "without_images")
                    }
                  >
                    Without Photos
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col xs={12} md={1}>
              <Button
                className="bk-btn-ghost w-100"
                onClick={handleReset}
                title="Reset filters"
              >
                <i className="bi bi-arrow-clockwise" />
              </Button>
            </Col>
          </Row>
        </div>

        {/* ACTIVE TAG */}
        {filterTag && (
          <div className="mb-3">
            <span
              className="bk-tag"
              onClick={() => handleFilterChange("tag", "")}
            >
              #{filterTag} <i className="bi bi-x ms-1" />
            </span>
          </div>
        )}

        {/* RESULTS INFO */}
        {meta.total > 0 && (
          <div className="mb-3">
            <small className="text-muted">
              Showing <strong>{meta.from || 0}</strong>–
              <strong>{meta.to || 0}</strong> of{" "}
              <strong>{meta.total || 0}</strong> ratings
              {hasActiveFilters && (
                <span className="ms-2">
                  <i className="bi bi-funnel me-1" />
                  Filtered
                </span>
              )}
            </small>
          </div>
        )}

        {/* REFRESH */}
        {loading && ratings.length > 0 && (
          <div className="text-center py-2 mb-2">
            <Spinner animation="border" size="sm" className="me-2" />
            Refreshing…
          </div>
        )}

        {/* GRID */}
        {ratings.length === 0 ? (
          <div className="bk-card">
            <div className="bk-empty">
              <div className="bk-empty-icon">
                <i className="bi bi-star" />
              </div>
              <h5 style={{ color: Colors.TEXT_DARK }}>No ratings found</h5>
              <p className="text-muted mb-0">
                {hasActiveFilters
                  ? "Try adjusting your search criteria."
                  : "No ratings have been submitted yet."}
              </p>
            </div>
          </div>
        ) : (
          <Row className="g-3">
            {ratings.map((rating) => {
              const ratingValue = Number(rating.rating) || 0;
              const ratingMeta = getRatingMeta(ratingValue);
              const tags = normalizeTags(rating.tags);
              const images = Array.isArray(rating.images) ? rating.images : [];
              const responseAt =
                rating.admin_response_at || rating.admin_responded_at;

              return (
                <Col key={rating.rating_id} xs={12} lg={6} xl={4}>
                  <div className="bk-rating-card">
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                      <div
                        className="d-flex align-items-center gap-2"
                        style={{ minWidth: 0 }}
                      >
                        <div className="bk-rating-avatar">
                          {rating.is_anonymous ? (
                            <i className="bi bi-incognito" />
                          ) : (
                            initials(rating.passenger?.name)
                          )}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 700,
                              color: Colors.TEXT_DARK,
                              fontSize: 14,
                            }}
                          >
                            {rating.is_anonymous
                              ? "Anonymous Passenger"
                              : rating.passenger?.name || "Unknown Passenger"}
                          </div>
                          <small
                            className="text-muted d-block"
                            style={{ fontSize: 11 }}
                          >
                            <i className="bi bi-truck me-1" />#
                            {rating.rental?.jeepney_id || "N/A"}
                            {" · "}
                            <i className="bi bi-person-badge me-1" />
                            {rating.operator?.name || "N/A"}
                          </small>
                        </div>
                      </div>
                      <div className="text-end">
                        {renderStars(ratingValue)}
                        <div style={{ marginTop: 4 }}>
                          <span
                            className="bk-pill no-dot"
                            style={{
                              color: ratingMeta.color,
                              background: ratingMeta.bg,
                              fontSize: 11,
                              padding: "3px 8px",
                            }}
                          >
                            {getRatingLabel(ratingValue)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Categories */}
                    <div className="mb-3">
                      <div
                        className="bk-section-title"
                        style={{ marginBottom: 8 }}
                      >
                        <i className="bi bi-grid" /> Categories
                      </div>
                      <Row className="g-2">
                        {CATEGORIES.map((cat) => (
                          <Col xs={6} key={cat}>
                            <div className="bk-cat-box">
                              <span className="bk-cat-label">
                                {getCategoryLabel(cat)}
                              </span>
                              {renderStars(
                                rating[`${cat}_rating`] || ratingValue,
                                true,
                              )}
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>

                    {/* Comment */}
                    {rating.comment && (
                      <div className="mb-3">
                        <div
                          className="bk-comment-box"
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          <i
                            className="bi bi-chat-left-quote me-2"
                            style={{ color: Colors.PRIMARY }}
                          />
                          {rating.comment}
                        </div>
                      </div>
                    )}

                    {/* Tags */}
                    {tags.length > 0 && (
                      <div className="mb-3 d-flex flex-wrap gap-1">
                        {tags.map((tag, i) => (
                          <span
                            key={i}
                            className="bk-tag"
                            onClick={() => handleFilterChange("tag", tag)}
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Images */}
                    {images.length > 0 && (
                      <div className="mb-3 d-flex gap-2 flex-wrap">
                        {images.slice(0, 4).map((image, i) => {
                          const url = getImageUrl(image);
                          if (!url) return null;
                          return (
                            <div
                              key={i}
                              className="bk-thumb-wrap"
                              onClick={() => openImageGallery(images)}
                            >
                              <img
                                src={url}
                                alt={`Review ${i + 1}`}
                                className="bk-thumb"
                                loading="lazy"
                              />
                              {i === 3 && images.length > 4 && (
                                <div className="bk-thumb-overlay">
                                  +{images.length - 4}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Admin response */}
                    {rating.admin_response && (
                      <div className="mb-3">
                        <div className="bk-response-box">
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: ".06em",
                              color: SECONDARY_BLUE,
                              marginBottom: 6,
                            }}
                          >
                            <i className="bi bi-shield-check me-1" />
                            Admin Response
                          </div>
                          <div
                            style={{
                              whiteSpace: "pre-wrap",
                              color: Colors.TEXT_DARK,
                            }}
                          >
                            {rating.admin_response}
                          </div>
                          <small
                            className="text-muted d-block mt-2"
                            style={{ fontSize: 11 }}
                          >
                            <i className="bi bi-clock me-1" />
                            {formatDateTime(responseAt)}
                          </small>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div
                      className="mt-auto pt-3"
                      style={{ borderTop: T.border }}
                    >
                      <div className="d-flex gap-2 flex-wrap align-items-center justify-content-between">
                        <div className="d-flex gap-1 flex-wrap">
                          {rating.is_anonymous && (
                            <span
                              className="bk-pill no-dot"
                              style={{
                                color: Colors.GRAY,
                                background: `${Colors.GRAY}26`,
                                fontSize: 11,
                              }}
                            >
                              <i className="bi bi-incognito me-1" />
                              Anonymous
                            </span>
                          )}
                          {ratingValue > 0 && ratingValue <= 2 && (
                            <span
                              className="bk-pill no-dot"
                              style={{
                                color: Colors.RED,
                                background: `${Colors.RED}1f`,
                                fontSize: 11,
                              }}
                            >
                              <i className="bi bi-exclamation-triangle me-1" />
                              Low Rating
                            </span>
                          )}
                          {!rating.admin_response ? (
                            <span
                              className="bk-pill no-dot"
                              style={{
                                color: Colors.WARNING,
                                background: `${Colors.WARNING}1f`,
                                fontSize: 11,
                              }}
                            >
                              <i className="bi bi-clock me-1" />
                              Pending
                            </span>
                          ) : (
                            <span
                              className="bk-pill no-dot"
                              style={{
                                color: Colors.SUCCESS,
                                background: `${Colors.SUCCESS}1f`,
                                fontSize: 11,
                              }}
                            >
                              <i className="bi bi-check-circle me-1" />
                              Responded
                            </span>
                          )}
                        </div>

                        <div className="d-flex gap-1">
                          <Button
                            className="bk-btn-ghost"
                            size="sm"
                            onClick={() => openAdminResponseModal(rating)}
                          >
                            <i
                              className={`bi ${
                                rating.admin_response ? "bi-pencil" : "bi-reply"
                              } me-1`}
                            />
                            {rating.admin_response ? "Edit" : "Respond"}
                          </Button>
                          {rating.admin_response && (
                            <Button
                              className="bk-btn-danger"
                              size="sm"
                              onClick={() =>
                                deleteAdminResponse(rating.rating_id)
                              }
                            >
                              <i className="bi bi-trash" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="text-muted mt-2" style={{ fontSize: 11 }}>
                        <i className="bi bi-calendar3 me-1" />
                        {formatDateTime(rating.created_at)}
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-4">
            <small className="text-muted">
              Page {page} of {totalPages}
            </small>
            <Pagination className="mb-0">
              <Pagination.Prev
                disabled={page === 1}
                onClick={() => handlePageChange(page - 1)}
              />
              {renderPaginationItems()}
              <Pagination.Next
                disabled={page === totalPages}
                onClick={() => handlePageChange(page + 1)}
              />
            </Pagination>
          </div>
        )}

        {/* ADMIN RESPONSE MODAL */}
        <Modal
          show={showAdminResponseModal}
          onHide={closeAdminResponseModal}
          size="lg"
          centered
          backdrop={submittingResponse ? "static" : true}
        >
          <Modal.Header closeButton={!submittingResponse}>
            <Modal.Title>
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: `${Colors.PRIMARY}1f`,
                    color: Colors.PRIMARY,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <i className="bi bi-reply" />
                </span>
                {selectedRating?.admin_response
                  ? "Edit Admin Response"
                  : "Add Admin Response"}
              </div>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {selectedRating && (
              <>
                <div className="bk-section">
                  <div className="bk-section-title">
                    <i className="bi bi-chat-left-quote" />
                    Customer Review
                  </div>

                  <div className="d-flex align-items-center gap-2 mb-2">
                    {renderStars(selectedRating.rating || 0)}
                    <strong style={{ color: Colors.TEXT_DARK }}>
                      {getRatingLabel(selectedRating.rating || 0)}
                    </strong>
                  </div>

                  <div className="mb-2" style={{ fontSize: 13 }}>
                    <strong>Passenger:</strong>{" "}
                    {selectedRating.is_anonymous
                      ? "Anonymous"
                      : selectedRating.passenger?.name || "Unknown"}
                  </div>

                  <div className="mb-2" style={{ fontSize: 13 }}>
                    <strong>Operator:</strong>{" "}
                    {selectedRating.operator?.name || "Unknown"}
                  </div>

                  {selectedRating.comment && (
                    <div
                      className="bk-comment-box mt-3"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      <i
                        className="bi bi-chat-left-quote me-2"
                        style={{ color: Colors.PRIMARY }}
                      />
                      {selectedRating.comment}
                    </div>
                  )}

                  {normalizeTags(selectedRating.tags).length > 0 && (
                    <div className="mt-3 d-flex flex-wrap gap-1">
                      {normalizeTags(selectedRating.tags).map((tag, i) => (
                        <span key={i} className="bk-tag">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {Array.isArray(selectedRating.images) &&
                    selectedRating.images.length > 0 && (
                      <div className="mt-3 d-flex gap-2 flex-wrap">
                        {selectedRating.images.slice(0, 4).map((image, i) => {
                          const url = getImageUrl(image);
                          if (!url) return null;
                          return (
                            <img
                              key={i}
                              src={url}
                              alt={`Review ${i + 1}`}
                              className="bk-thumb"
                              loading="lazy"
                              onClick={() =>
                                openImageGallery(selectedRating.images)
                              }
                            />
                          );
                        })}
                        {selectedRating.images.length > 4 && (
                          <div className="d-flex align-items-center">
                            <span
                              className="bk-pill no-dot"
                              style={{
                                color: Colors.GRAY,
                                background: `${Colors.GRAY}26`,
                              }}
                            >
                              +{selectedRating.images.length - 4} more
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                </div>

                <Form.Group>
                  <Form.Label
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: Colors.TEXT_DARK,
                    }}
                  >
                    Your Response
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={adminResponse}
                    onChange={(e) => setAdminResponse(e.target.value)}
                    placeholder="Enter a professional response to this customer review…"
                    maxLength={MAX_RESPONSE_LENGTH}
                    disabled={submittingResponse}
                    style={{ borderRadius: T.radiusSm, fontSize: 14 }}
                  />
                  <Form.Text className="text-muted">
                    {adminResponse.length}/{MAX_RESPONSE_LENGTH} characters
                  </Form.Text>
                </Form.Group>

                {responseError && (
                  <Alert variant="danger" className="mt-3 mb-0">
                    <i className="bi bi-exclamation-triangle me-2" />
                    {responseError}
                  </Alert>
                )}
              </>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              className="bk-btn-ghost"
              onClick={closeAdminResponseModal}
              disabled={submittingResponse}
            >
              Cancel
            </Button>
            <Button
              className="bk-btn-primary"
              onClick={submitAdminResponse}
              disabled={submittingResponse || !adminResponse.trim()}
            >
              {submittingResponse ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  {selectedRating?.admin_response ? "Updating…" : "Submitting…"}
                </>
              ) : (
                <>
                  <i className="bi bi-send me-1" />
                  {selectedRating?.admin_response
                    ? "Update Response"
                    : "Submit Response"}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* IMAGE MODAL */}
        <Modal
          show={showImageModal}
          onHide={() => setShowImageModal(false)}
          size="xl"
          centered
          dialogClassName="bk-image-modal"
          contentClassName="bg-transparent border-0"
        >
          <Modal.Body
            className="p-0 position-relative"
            style={{ background: "transparent" }}
          >
            <Button
              className="btn-close btn-close-white position-absolute top-0 end-0 m-3"
              style={{ zIndex: 10 }}
              onClick={() => setShowImageModal(false)}
            />
            {selectedImages[currentImageIndex] && (
              <>
                <img
                  src={getImageUrl(selectedImages[currentImageIndex])}
                  alt={`Review ${currentImageIndex + 1}`}
                  className="img-fluid mx-auto d-block"
                  style={{ maxHeight: "80vh", objectFit: "contain" }}
                />
                {selectedImages.length > 1 && (
                  <>
                    <button
                      className="bk-image-nav"
                      style={{ left: 12 }}
                      onClick={prevImage}
                    >
                      <i className="bi bi-chevron-left" />
                    </button>
                    <button
                      className="bk-image-nav"
                      style={{ right: 12 }}
                      onClick={nextImage}
                    >
                      <i className="bi bi-chevron-right" />
                    </button>
                    <div className="position-absolute bottom-0 start-50 translate-middle-x mb-3">
                      <span
                        className="bk-pill no-dot"
                        style={{ color: "#fff", background: "rgba(0,0,0,.7)" }}
                      >
                        {currentImageIndex + 1} / {selectedImages.length}
                      </span>
                    </div>
                  </>
                )}
              </>
            )}
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default ViewRatings;
